import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { notification } from 'antd';

import Header from 'components/Pages/Admin/CheckinPage/QrCodeScanner/Header';
import useQuery from 'hooks/useQuery';
import {
  Html5Qrcode,
  type Html5QrcodeCameraScanConfig,
  type Html5QrcodeFullConfig,
  Html5QrcodeSupportedFormats
} from 'html5-qrcode';
import attendanceListService from 'services/attendanceList';
import eventStaffService from 'services/eventStaff';

import { ManualCheckin } from './ManualCheckin';
import ScanContent from './ScanContent';
import Scanner from './Scanner';
import styles from './styles.module.css';
import { Attendance, StaffUser } from './types';

const mobileAspectRatio = 0.7;
const desktopAspectRatio = 1.3;

const config: Html5QrcodeCameraScanConfig = {
  fps: 5,
  qrbox: 200,
  aspectRatio: window.innerWidth < 768 ? mobileAspectRatio : desktopAspectRatio,
  videoConstraints: {
    facingMode: 'environment'
  }
};

const fullConfig: Html5QrcodeFullConfig = {
  formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
  verbose: false
};

const REQUEST_DELAY = 1000;

const QrCodeScanner = () => {
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [attendance, setAttendance] = useState<Attendance | null>(null);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showLastCheckinMessage, setShowLastCheckinMessage] = useState(false);
  const [showNotAuthorizedMessage, setShowNotAuthorizedMessage] = useState(false);
  const [showLastNotAuthorizedMessage, setShowLastNotAuthorizedMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [staffUser, setStaffUser] = useState<StaffUser | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [manualCheckin, setManualCheckin] = useState(false);

  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const sendingRequest = useRef(false);
  const query = useQuery();

  const html5QrCodeRef = useRef(null);
  const delayRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const staffToken = useMemo(() => {
    const staffToken = query.get('token');

    if (staffToken) {
      localStorage.setItem('staffToken', staffToken);
      return staffToken;
    }

    const storedToken = localStorage.getItem('staffToken');

    if (storedToken) {
      return storedToken;
    }

    return null;
  }, [query]);

  const updateStaffUser = useCallback(() => {
    if (!staffToken) return;

    setLoadingDetails(true);
    const subscription = eventStaffService.getStaffDetails(staffToken).subscribe({
      next: staffData => {
        setStaffUser({
          id: staffData.id,
          name: staffData.name,
          email: staffData.email,
          checkins: staffData.checkins
        });
        setLoadingDetails(false);
      },
      error: () => {
        notification.error({
          message: 'Erro ao buscar dados do credenciador. Verifique o link e tente novamente.'
        });
        setLoadingDetails(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [staffToken]);

  useEffect(() => {
    if (!staffToken) return;

    if (staffUser) return;

    setLoadingDetails(true);
    const subscription = eventStaffService.getStaffDetails(staffToken).subscribe({
      next: staffData => {
        setStaffUser({
          id: staffData.id,
          name: staffData.name,
          email: staffData.email,
          checkins: staffData.checkins
        });
        setLoadingDetails(false);
      },
      error: () => {
        notification.error({
          message: 'Erro ao buscar dados do credenciador. Verifique o link e tente novamente.'
        });
        setLoadingDetails(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [query, staffToken, staffUser]);

  const handleSuccessMessage = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setShowNotAuthorizedMessage(false);
    setShowLastNotAuthorizedMessage(false);
    setShowLastCheckinMessage(false);
    setShowSuccessMessage(true);

    const timerId = setTimeout(() => {
      setShowNotAuthorizedMessage(false);
      setShowLastNotAuthorizedMessage(false);
      setShowSuccessMessage(false);
      setShowLastCheckinMessage(true);
    }, 5000);
    timerRef.current = timerId;
  }, []);

  const handleNotAuthorizedMessage = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setShowSuccessMessage(false);
    setShowLastCheckinMessage(false);
    setShowNotAuthorizedMessage(true);

    const timerId = setTimeout(() => {
      setShowSuccessMessage(false);
      setShowLastCheckinMessage(false);
      setShowNotAuthorizedMessage(false);
      setShowLastNotAuthorizedMessage(true);
    }, 5000);
    timerRef.current = timerId;
  }, []);

  const setSendingRequest = (value: boolean) => {
    sendingRequest.current = value;
    setIsSendingRequest(value);
  };

  const qrCodeSuccessCallback = useCallback(
    (decodedText: string) => {
      if (sendingRequest.current || Date.now() - delayRef.current < REQUEST_DELAY) {
        return;
      }

      setShowLastCheckinMessage(false);
      setShowSuccessMessage(false);
      setShowNotAuthorizedMessage(false);
      setShowLastNotAuthorizedMessage(false);
      setErrorMessage(null);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      try {
        setSendingRequest(true);
        const url = new URL(decodedText);

        if (!url.pathname.match(/confirm/)) {
          setErrorMessage('Erro ao processar o QR Code. Por favor, direcione o participante ao atendimento.');
          handleNotAuthorizedMessage();
          setSendingRequest(false);
          return;
        }

        const hash = url.pathname.split('/')[2];

        if (!staffToken) {
          setErrorMessage('Link inválido. Entre em contato com o produtor do evento.');
          setSendingRequest(false);
          return;
        }

        if (!navigator.onLine) {
          setSendingRequest(true);
          return;
        }

        delayRef.current = Date.now();
        attendanceListService.checkinV2(hash, staffToken).subscribe({
          next: attendanceData => {
            setAttendance(attendanceData);

            setSendingRequest(false);

            if (attendanceData?.checkin_date) {
              handleNotAuthorizedMessage();
              setErrorMessage('Este ingresso já foi utilizado. Por favor, direcione o participante ao atendimento.');
              return;
            }

            handleSuccessMessage();
          },
          error: err => {
            setSendingRequest(false);
            handleNotAuthorizedMessage();

            if (err?.data?.code === 'ERR_CUSTOM') {
              setErrorMessage(err?.data?.details);
              return;
            }

            if (err?.data?.code === 'ERR_FBA403') {
              setErrorMessage('Você não tem permissão para realizar esta ação.');
              return;
            }

            setErrorMessage('Erro ao processar o QR Code. Por favor, direcione o participante ao atendimento.');
          },
          complete: () => {
            delayRef.current = Date.now();
          }
        });
      } catch {
        setErrorMessage('O QR Code escaneado não é válido. Tente novamente.');
      }
    },
    [handleNotAuthorizedMessage, handleSuccessMessage, staffToken]
  );

  const requestPermission = () => {
    Html5Qrcode.getCameras()
      .then(cameras => {
        if (cameras && cameras.length) {
          setCameraId(cameras[0].id);
        }
      })
      .catch(err => {
        console.error('Error getting cameras:', err);
        notification.error({
          message: 'É preciso autorizar o acesso a câmera. Recarregue a página e tente novamente'
        });
        setScannerLoading(false);
      });
  };

  const startScanner = () => {
    if (!cameraId) return;

    setScannerLoading(true);
    html5QrCodeRef.current = new Html5Qrcode('qr-reader', fullConfig);
    html5QrCodeRef.current
      .start(cameraId, config, qrCodeSuccessCallback, undefined)
      .then(() => {
        setScannerLoading(false);
      })
      .catch(err => {
        console.error('Error starting QR code scanner:', err);
      });
  };

  const restartScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current
        .stop()
        .then(() => {
          setScannerLoading(true);
          setTimeout(() => {
            html5QrCodeRef.current
              .start(cameraId, config, qrCodeSuccessCallback, undefined)
              .then(() => {
                setScannerLoading(false);
              })
              .catch(err => {
                console.error('Error starting QR code scanner:', err);
              });
          }, 500);
        })
        .catch(err => {
          console.error('Error stopping QR code scanner:', err);
        });
    }
  };

  const clearScanContent = () => {
    setAttendance(null);
    setShowSuccessMessage(false);
    setShowLastCheckinMessage(false);
    setShowNotAuthorizedMessage(false);
    setShowLastNotAuthorizedMessage(false);
    setErrorMessage(null);

    if (timerRef?.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <Header
          staffToken={staffToken}
          staffUser={staffUser}
          loading={loadingDetails}
          updateStaffUser={updateStaffUser}
          manualCheckin={manualCheckin}
          changeCheckin={() => setManualCheckin(prev => !prev)}
          openManualCheckin={() => setManualCheckin(true)}
          clearScanContent={clearScanContent}
        />
        <Scanner
          loading={scannerLoading}
          scannerState={html5QrCodeRef.current?.getState()}
          restartScanner={restartScanner}
        />

        <ScanContent
          showSuccessMessage={showSuccessMessage}
          showLastCheckinMessage={showLastCheckinMessage}
          showLoadingMessage={isSendingRequest}
          showNotAuthorizedMessage={showNotAuthorizedMessage}
          showLastNotAuthorizedMessage={showLastNotAuthorizedMessage}
          attendance={attendance}
          cameraId={cameraId}
          scannerState={html5QrCodeRef.current?.getState()}
          requestPermission={requestPermission}
          startScanner={startScanner}
          errorMessage={errorMessage}
          staffToken={staffToken}
          clearScanContent={clearScanContent}
        />
      </div>

      <ManualCheckin
        staffToken={staffToken}
        open={manualCheckin}
        onClose={() => {
          setManualCheckin(false);
          clearScanContent();
        }}
      />
    </div>
  );
};

export default QrCodeScanner;
